// ===============================================================
// ✅ EditProfileForm.js — تعديل بروفايل + نظام صور (نسخة 2025) — LIVORA SAFE (FINAL)
// - birthDate READ ONLY (server-only)
// - Store ONLY Storage paths in Firestore (photos[] / profileImage)
// - Resolve paths to downloadURL for UI rendering
// - Backward compatible with old http URLs
// path: src/components/EditProfileForm.js
// ===============================================================

import React, { useEffect, useState, useMemo } from "react";
import "./EditProfileForm.css";

import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, deleteObject } from "firebase/storage";

import imageCompression from "browser-image-compression";
import { useTranslation } from "react-i18next";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { resolveProfilePhotoUrlCore } from "../utils/profilePhotoResolver";
import { selectFinalPhotos } from "../utils/profilePhotoSelection";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "./common/LivoraToast";
import { parseBirthDateParts, formatBirthDateDisplay } from "../utils/localizedDate";
import { NAME_MAX_LENGTH, validateDisplayName } from "../utils/displayName";

const MAX_PHOTOS = 4;
const UPLOAD_CONCURRENCY = 3;

// -------------------------------------------------------------
// 🧩 Helpers
// -------------------------------------------------------------
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

// Convert stored value (either storage path or old downloadURL) -> storage path (if possible)
function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return null;

  // If it's already a path like "profileImages/uid/file.jpg"
  if (!isHttpUrl(value)) return value;

  // If it's a downloadURL -> extract path after /o/
  try {
    const part = value.split("/o/")[1];
    if (!part) return null;
    return decodeURIComponent(part.split("?")[0]); // turns %2F into /
  } catch {
    return null;
  }
}

async function mapWithConcurrency(items, concurrency, mapper) {
  if (!items.length) return [];

  const results = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
}

async function deletePhotosInParallel(photosToDelete) {
  if (!photosToDelete.length) return;

  const results = await Promise.allSettled(
    photosToDelete.map(async (value) => {
      const path = storagePathFromValue(value);
      if (!path) return;
      await deleteObject(ref(storage, path));
    })
  );

  results.forEach((result, index) => {
    if (result.status === "rejected") {
      console.warn("[editProfile] delete failed", {
        value: photosToDelete[index],
        error: result.reason?.message || result.reason,
      });
    }
  });
}

function EditProfileForm() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast, showToast } = useLivoraToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStage, setSaveStage] = useState("idle");
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState(""); // ✅ server-only (read-only)

  // Stored values from Firestore: may be paths or old http URLs
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);

  // New local files + previews (dataURL)
  const [newPhotos, setNewPhotos] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [userID, setUserID] = useState("");
  const [userGender, setUserGender] = useState("");

  // UI resolved URLs
  const [uiPhotos, setUiPhotos] = useState([]);

  // -------------------------------------------------------------
  // 🔥 تحميل بيانات المستخدم
  // -------------------------------------------------------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const current = auth.currentUser;
        if (!current) return navigate("/");

        setUserID(current.uid);

        const refUser = doc(db, "users", current.uid);
        const snap = await getDoc(refUser);

        if (snap.exists()) {
          const data = snap.data() || {};

          setName(data.name || "");
          setUserGender(String(data.gender || ""));
          // A native <input type="date"> requires its `value` in exact
          // "YYYY-MM-DD" form (never a locale-formatted display string,
          // which would just make the control appear empty) - so the
          // canonical field is routed through the same strict parser as
          // every other birth-date consumer (parseBirthDateParts) purely
          // to validate it, and the original ISO string is kept as-is
          // once validated. Malformed/legacy data never falls through to
          // the DOM as an invented value; it degrades to "".
          const rawBirthDate = data.birthDate || data.birth || "";
          setBirthDate(parseBirthDateParts(rawBirthDate) ? rawBirthDate : "");

          if (Array.isArray(data.photos)) {
            setExistingPhotos(data.photos.filter(Boolean));
          } else if (data.profileImage) {
            setExistingPhotos([data.profileImage].filter(Boolean));
          } else {
            setExistingPhotos([]);
          }
        }
      } catch (err) {
        console.error(err);
        setError(t("load_error", { defaultValue: "حدث خطأ أثناء تحميل البيانات" }));
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate, t]);

  // -------------------------------------------------------------
  // 🖼️ تجميع الصور (قيم خام للمنطق)
  // - existingPhotos: paths/http
  // - newPreviews: dataURL
  // -------------------------------------------------------------
  const allPhotosForUI = useMemo(() => {
    const kept = existingPhotos.filter((v) => !photosToDelete.includes(v));
    return [...kept, ...newPreviews];
  }, [existingPhotos, photosToDelete, newPreviews]);

  // -------------------------------------------------------------
  // 🖼️ تحويل الصور للعرض (downloadURL)
  // - paths -> downloadURL
  // - http URLs stay as-is
  // - dataURL stay as-is
  // -------------------------------------------------------------
  useEffect(() => {
    let alive = true;

    const run = async () => {
      const resolved = [];
      for (const item of allPhotosForUI) {
        // dataURL preview
        if (typeof item === "string" && item.startsWith("data:")) {
          resolved.push(item);
          continue;
        }

        const url = await resolveProfilePhotoUrlCore(item);
        if (url) resolved.push(url);
      }

      if (alive) {
        setUiPhotos(resolved);

        if (resolved.length === 0) setCurrentIndex(0);
        else if (currentIndex >= resolved.length) setCurrentIndex(0);
      }
    };

    run();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPhotosForUI]);

  // -------------------------------------------------------------
  // 📤 اختيار الصور الجديدة
  // -------------------------------------------------------------
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const kept = existingPhotos.length - photosToDelete.length;
    const slotsLeft = MAX_PHOTOS - kept - newPhotos.length;

    if (slotsLeft <= 0) {
      alert(
        t("max_photos", {
          defaultValue: "وصلت للحد الأقصى من الصور",
          count: MAX_PHOTOS,
        })
      );
      return;
    }

    const selected = files.slice(0, slotsLeft);

    selected.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result;
        if (typeof result === "string") {
          setNewPreviews((prev) => [...prev, result]);
        }
      };
      reader.readAsDataURL(file);
    });

    setNewPhotos((prev) => [...prev, ...selected]);

    // reset input so selecting same file again works
    e.target.value = "";
  };

  // -------------------------------------------------------------
  // 🗑️ حذف الصورة الحالية من السلايدر
  // (تتعامل مع uiPhotos في العرض لكن المنطق على allPhotosForUI)
  // -------------------------------------------------------------
  const handleDeleteMainPhoto = () => {
    const currentRaw = allPhotosForUI[currentIndex];
    if (!currentRaw) return;

    // existing photo (path/http)
    if (existingPhotos.includes(currentRaw)) {
      setPhotosToDelete((p) => [...p, currentRaw]);
    } else {
      // it's a preview (dataURL) -> remove from newPreviews + newPhotos with same index in preview set
      const idx = newPreviews.indexOf(currentRaw);
      if (idx !== -1) {
        setNewPreviews((p) => p.filter((_, i) => i !== idx));
        setNewPhotos((p) => p.filter((_, i) => i !== idx));
      }
    }

    setCurrentIndex(0);
  };

  // -------------------------------------------------------------
  // 💾 حفظ التعديلات
  // -------------------------------------------------------------
  const getSaveStageLabel = (stage) => {
    switch (stage) {
      case "saving_data":
        return t("edit_profile_status.saving_data", {
          defaultValue: "جارٍ حفظ البيانات...",
        });
      case "preparing_photos":
        return t("edit_profile_status.preparing_photos", {
          defaultValue: "جارٍ تجهيز الصور...",
        });
      case "uploading_photos":
        return t("edit_profile_status.uploading_photos", {
          defaultValue: "جارٍ رفع الصور...",
        });
      case "done":
        return t("edit_profile_status.save_done", { defaultValue: "تم الحفظ" });
      default:
        return "";
    }
  };

  const finishSaveSuccess = () => {
    const message = t("save_success", {
      defaultValue: "تم حفظ التعديلات بنجاح",
    });
    setSaveStage("done");
    showToast("success", message);
    navigate("/my-profile");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const saveStartedAt = Date.now();
    console.log("[editProfile] saveStart", { at: saveStartedAt });

    setSaving(true);
    setSaveStage("saving_data");
    setError("");

    try {
      const current = auth.currentUser;
      if (!current) {
        throw new Error(t("login_required", { defaultValue: "يجب تسجيل الدخول أولاً" }));
      }

      const uid = current.uid;
      const userRef = doc(db, "users", uid);

      // Validated/normalized BEFORE any photo delete/upload work starts,
      // so an invalid name fails fast and never leaves photos deleted
      // with no corresponding save. The existing stored name is left
      // completely untouched (no updateDoc call happens at all below)
      // when this fails - see src/utils/displayName.js, the exact same
      // rules SelectGenderPage.js's onboarding flow uses.
      const nameValidation = validateDisplayName(name);
      if (!nameValidation.ok) {
        setError(
          t(nameValidation.errorKey, {
            defaultValue:
              nameValidation.errorKey === "gender.name_too_long"
                ? "Name is too long."
                : "Enter your name first.",
          })
        );
        setSaveStage("idle");
        return;
      }

      const hasNewPhotos = newPhotos.length > 0;
      const hasDeletes = photosToDelete.length > 0;

      if (!hasNewPhotos && !hasDeletes) {
        const updateStart = Date.now();
        await updateDoc(userRef, {
          name: nameValidation.value,
          updatedAt: serverTimestamp(),
        });
        console.log("[editProfile] updateDocMs", {
          ms: Date.now() - updateStart,
          fastPath: true,
        });
        console.log("[editProfile] totalMs", {
          ms: Date.now() - saveStartedAt,
          fastPath: true,
        });
        finishSaveSuccess();
        return;
      }

      if (hasDeletes) {
        const deleteStart = Date.now();
        await deletePhotosInParallel(photosToDelete);
        console.log("[editProfile] deleteMs", {
          ms: Date.now() - deleteStart,
          count: photosToDelete.length,
        });
      }

      let uploadedPaths = [];
      if (hasNewPhotos) {
        setSaveStage("preparing_photos");
        const uploadStart = Date.now();
        let uploadStageSet = false;
        const batchStamp = Date.now();

        uploadedPaths = await mapWithConcurrency(
          newPhotos,
          UPLOAD_CONCURRENCY,
          async (file, index) => {
            setSaveStage("preparing_photos");

            const compressed = await imageCompression(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1080,
              useWebWorker: true,
            });

            if (!uploadStageSet) {
              uploadStageSet = true;
              setSaveStage("uploading_photos");
            }

            const fileName = `${batchStamp}_${index}.jpg`;
            const path = `profileImages/${uid}/${fileName}`;
            const fileRef = ref(storage, path);

            await uploadBytes(fileRef, compressed);
            return path;
          }
        );

        console.log("[editProfile] uploadMs", {
          ms: Date.now() - uploadStart,
          count: newPhotos.length,
        });
      }

      const keptExisting = existingPhotos
        .filter((v) => !photosToDelete.includes(v))
        .map((v) => storagePathFromValue(v) || v)
        .filter(Boolean);

      const { finalPhotos, profileImage } = selectFinalPhotos({
        keptExisting,
        uploadedPaths,
        maxPhotos: MAX_PHOTOS,
      });

      setSaveStage("saving_data");
      const updateStart = Date.now();
      await updateDoc(userRef, {
        name: nameValidation.value,
        photos: finalPhotos,
        profileImage,
        updatedAt: serverTimestamp(),
      });
      console.log("[editProfile] updateDocMs", {
        ms: Date.now() - updateStart,
        fastPath: false,
      });
      console.log("[editProfile] totalMs", {
        ms: Date.now() - saveStartedAt,
        fastPath: false,
      });

      finishSaveSuccess();
    } catch (err) {
      console.error("[editProfile] save failed", err);
      setError(t("save_error", { defaultValue: "حدث خطأ أثناء الحفظ، حاول مرة أخرى" }));
      setSaveStage("idle");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-box">
        <div className="spinner"></div>
        <p>{t("loading_data", { defaultValue: "جارٍ تحميل البيانات..." })}</p>
      </div>
    );
  }

  // -------------------------------------------------------------
  // 🎨 الواجهة
  // -------------------------------------------------------------
  const mainSrc =
    uiPhotos.length && uiPhotos[currentIndex]
      ? uiPhotos[currentIndex]
      : defaultAvatarByGender(userGender);

  const mainPhotoFallback = defaultAvatarByGender(userGender);

  const saveStageLabel = saving ? getSaveStageLabel(saveStage) : "";

  return (
    <div className="edit-form-page">
      <LivoraToast toast={toast} />

      <h2 className="edit-title">
        {t("edit_profile", { defaultValue: "تعديل الملف الشخصي" })}
      </h2>

      {error && <p className="edit-form-error">{error}</p>}
      {saveStageLabel && (
        <p className="edit-save-status" aria-live="polite">
          {saveStageLabel}
        </p>
      )}

      {/* 🔥 الصورة الكبيرة */}
      <div className="slider-box">
        {allPhotosForUI.length > 0 && (
          <button className="main-delete-btn" onClick={handleDeleteMainPhoto} type="button">
            ✕
          </button>
        )}

        <img
          src={mainSrc}
          alt="profile"
          className="profile-photo"
          onError={(e) => {
            e.currentTarget.src = mainPhotoFallback;
          }}
        />

        <label className="add-photo-btn" title={t("add_photo", { defaultValue: "إضافة صورة" })}>
          +
          <input
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </label>
      </div>

      {/* 🔥 الصور الصغيرة */}
      <div className="small-photos-row">
        {uiPhotos.map((photo, index) => (
          <div
            key={index}
            className={`small-photo-box${
              index === currentIndex ? " small-photo-box--active" : ""
            }`}
            onClick={() => setCurrentIndex(index)}
            role="button"
            tabIndex={0}
            aria-current={index === currentIndex ? "true" : undefined}
          >
            <img
              src={photo}
              alt=""
              onError={(e) => {
                e.currentTarget.src = mainPhotoFallback;
              }}
            />
          </div>
        ))}

        {uiPhotos.length < MAX_PHOTOS && (
          <div className="small-photo-box">
            <label className="add-photo-small" title={t("add_photo", { defaultValue: "إضافة صورة" })}>
              +
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </label>
          </div>
        )}
      </div>

      {/* 🔥 البيانات الأساسية */}
      <form onSubmit={handleSave}>
        <label className="field-label">
          {t("user_id", { defaultValue: "معرّف المستخدم" })}
        </label>
        <input className="text-input" value={userID} disabled style={{ background: "#222", color: "#aaa" }} />

        <label className="field-label">{t("name", { defaultValue: "الاسم" })}</label>
        <input
          className="text-input"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          onChange={(e) => setName(e.target.value)}
        />

        {/* ✅ تاريخ الميلاد — عرض فقط (server-only), locked, never editable
            here. A native <input type="date"> would localize its visible
            digits to the browser/OS locale even though its underlying
            value is ASCII, so the canonical ISO value is instead run
            through the shared, timezone-safe formatBirthDateDisplay
            (forces Latin/Western digits and UTC for a date-only value)
            and shown as plain read-only text - one accessible value, not
            a native date control duplicating it. Missing/malformed data
            renders an empty field, never an invented date. */}
        <label className="field-label">
          {t("birth_date", { defaultValue: "تاريخ الميلاد" })}
        </label>
        <input
          className="text-input"
          type="text"
          inputMode="numeric"
          dir="ltr"
          readOnly
          disabled
          value={formatBirthDateDisplay(birthDate, i18n.resolvedLanguage || i18n.language)}
          aria-label={t("birth_date", { defaultValue: "تاريخ الميلاد" })}
          title={t("birth_locked", { defaultValue: "تاريخ الميلاد يُحدد مرة واحدة ولا يمكن تغييره" })}
          style={{ background: "#222", color: "#aaa" }}
        />

        <button className="save-btn" disabled={saving} aria-busy={saving}>
          {saving
            ? saveStageLabel ||
              t("saving", { defaultValue: "جارٍ الحفظ..." })
            : t("save_changes", { defaultValue: "حفظ التعديلات" })}
        </button>

        <button className="cancel-btn" type="button" onClick={() => navigate(-1)}>
          {t("cancel", { defaultValue: "إلغاء" })}
        </button>
      </form>
    </div>
  );
}

export default EditProfileForm;